/**
 * TeamsSection: the signed-in user's teams. Switch between them, or delete
 * one you own. User-scoped (see sections.ts): under staff impersonation
 * this still lists the staff member's own teams.
 *
 * A delete runs in the background (TRA-1508): the click saves it and the
 * dialog closes, the team's row shows the step while the api works, the
 * list reads the deletes again every few seconds until none runs, and a
 * finished delete drops the team (deleting the active team moves you to
 * another one, as before). A failed delete shows its reason and a Retry.
 *
 * Team creation is deliberately absent: staff provision teams from the
 * Employees page (POST /api/internal/teams), which yields the clean slug.
 */

import { useState } from 'react'
import { Check, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { TeamDeleteDialog } from '@/components/shared/TeamDeleteDialog'
import { TeamDeleteRowStatus } from '@/components/shared/TeamDeleteRowStatus'
import {
  deletedToast,
  deletingToast,
  type TeamDeleteRowState,
} from '@/components/shared/teamDeletions'
import { EYEBROW } from '@/components/ui/eyebrow'
import { useAuth } from '@/contexts/AuthContext'
import { useTeam } from '@/contexts/TeamContext'
import { useOwnerTeamDeletions } from '@/hooks/useTeamDeletions'
import { useDeleteTeam, useTeamDeletePreview } from '@/hooks/useTeamSettings'

import { RowAction } from './RowAction'
import { SettingsList, SettingsListRow } from './SettingsList'
import { SettingsPageHeader } from './SettingsPageHeader'
import { SettingsSection } from './SettingsSection'
import { afterTeamVanished } from './teamDelete'

interface TeamRef {
  id: string
  name: string
}

export function TeamsSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { teams, activeTeamId, switchTeam, refetch, vanityOrg, impersonatingTeamId } = useTeam()
  const { signinSilent } = useAuth()
  // Vanity hosts are locked to their org (TRA-829): list only that team,
  // and no deletes — the other teams belong to the canonical host.
  const visibleTeams = vanityOrg === null ? teams : teams.filter((t) => t.id === activeTeamId)
  // Deleting is keyed by the caller (the API checks ownership by user, and
  // never reads the team header), so it works under impersonation. It is
  // hidden there anyway: every other section offers no writes while
  // impersonating, and this one should read the same.
  const canDelete = vanityOrg === null && teams.length > 1 && impersonatingTeamId === null
  const deleteMutation = useDeleteTeam()
  const [teamToDelete, setTeamToDelete] = useState<TeamRef | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const preview = useTeamDeletePreview(teamToDelete?.id ?? null)
  const deletions = useOwnerTeamDeletions({
    onVanished: async (record) => {
      // The list holds the teams you own, and the team's group goes in
      // the last step, so a finished delete leaves the list rather than
      // turning done. So does a team whose ownership moved: only one gone
      // from the fresh team list is deleted. Deleting the active team
      // moves you to another one; switchTeam reloads the page.
      const outcome = await afterTeamVanished({
        teamId: record.team_id,
        activeTeamId,
        refreshToken: signinSilent,
        refetchTeams: refetch,
        switchTeam,
      })
      if (outcome !== 'kept') toast.success(deletedToast(record.display_name))
    },
  })

  const handleDelete = (target: TeamRef, confirm: string): void => {
    setDeleteError(null)
    deleteMutation.mutate(
      { teamId: target.id, params: { confirm } },
      {
        onSuccess: (answer) => {
          setTeamToDelete(null)
          toast.success(deletingToast(target.name))
          if (answer.status === 202) deletions.started(answer.data)
        },
        onError: (error) => {
          setDeleteError(getApiErrorDetail(error) ?? 'Failed to delete team. Please try again.')
        },
      },
    )
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <SettingsSection
        title="Your teams"
        description={
          canDelete
            ? 'Switching reloads the app scoped to that team. Deleting a team removes everything it owns.'
            : 'Switching reloads the app scoped to that team.'
        }
      >
        {visibleTeams.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No teams found. Contact TrainLoop to get your team set up.
          </p>
        ) : (
          <SettingsList>
            {visibleTeams.map((team) => (
              <TeamRow
                key={team.id}
                name={team.name}
                slug={team.slug}
                isActive={team.id === activeTeamId}
                deletion={deletions.stateOf(team.id)}
                onSwitch={() => {
                  switchTeam(team.id)
                }}
                onDelete={
                  canDelete
                    ? () => {
                        setDeleteError(null)
                        setTeamToDelete({ id: team.id, name: team.name })
                      }
                    : null
                }
                onRetry={canDelete ? deletions.retry : null}
              />
            ))}
          </SettingsList>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Teams are provisioned by TrainLoop.{' '}
          <a
            href="mailto:support@trainloop.ai"
            className="text-foreground underline underline-offset-4 hover:text-primary"
          >
            Contact support
          </a>{' '}
          to add one.
        </p>
      </SettingsSection>

      <TeamDeleteDialog
        open={teamToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTeamToDelete(null)
        }}
        teamName={teamToDelete?.name ?? ''}
        preview={preview}
        isPending={deleteMutation.isPending}
        error={deleteError}
        onConfirm={(confirm) => {
          if (teamToDelete) handleDelete(teamToDelete, confirm)
        }}
      />
    </div>
  )
}

function TeamRow({
  name,
  slug,
  isActive,
  deletion,
  onSwitch,
  onDelete,
  onRetry,
}: {
  name: string
  slug: string
  isActive: boolean
  deletion: TeamDeleteRowState
  onSwitch: () => void
  onDelete: (() => void) | null
  onRetry: ((deletionId: string) => void) | null
}): React.JSX.Element {
  return (
    <SettingsListRow className="min-h-12 py-0">
      <span className="flex w-4 shrink-0 text-foreground" aria-hidden="true">
        {isActive && <Check className="h-4 w-4" />}
      </span>
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 py-2 text-left"
        aria-current={isActive ? 'true' : undefined}
        onClick={() => {
          if (!isActive) onSwitch()
        }}
      >
        <span className="text-sm text-foreground">{name}</span>
        <span className="font-mono text-xs text-muted-foreground">{slug}</span>
      </button>
      {isActive && <span className={EYEBROW}>Active</span>}
      {deletion.kind === 'idle' ? (
        onDelete !== null && (
          <RowAction label={`Delete ${name}`} icon={Trash2} destructive onClick={onDelete} />
        )
      ) : (
        <TeamDeleteRowStatus deletion={deletion} onRetry={onRetry} />
      )}
    </SettingsListRow>
  )
}
